import Guard from 'arg-guard'

import Property from './property'

/**
 * Defined structure of a user profile.
 * @export User
 * @class User
 */
export default class User {

    /**
     * Creates an instance of a User.
     * @param {Object} data Source data - From API or state.
     * @memberof User
     */
    constructor(data) {
        Guard.throwIf(data, 'data')

        this.identifier = data.userName
        this.email = data.email
        this.firstName = data.firstName
        this.lastName = data.lastName
        this.phoneNumber = data.contactTelephoneNumber
        this.contactStatus = data.contactStatus

        this.marketingConsentGranted = data.marketingConsentGiven
        this.marketingConsent = data.marketingConsentGiven
            ? data.marketingConsentGrantedOn ? `Granted on ${ data.marketingConsentGrantedOn }` : 'Granted'
            : data.marketingConsentWithdrawnOn ? `Withdrawn on ${ data.marketingConsentWithdrawnOn }` : 'Denied'

        if (data.evaluationConsentGiven !== undefined && data.evaluationConsentGiven !== null) {
            this.evaluationConsentGranted = data.evaluationConsentGiven
            this.evaluationConsent = data.evaluationConsentGiven
                ? data.evaluationConsentGrantedOn ? `Granted on ${ data.evaluationConsentGrantedOn }` : 'Granted'
                : data.evaluationConsentWithdrawnOn ? `Withdrawn on ${ data.evaluationConsentWithdrawnOn }` : 'Denied'
        }

        /**
         * @type {Array<Property>}
         */
        this.addresses = data.addresses ? data.addresses.map(a => Property.ensure(a)) : []

        /**
         * Previously set partner link id, if available.
         * @type {?number}
         */
        this.partnerLinkId = data.partnerLinkId
    }

    /*
     * The full name of the user.
     */
    get fullName() {
        return this.firstName
            ? this.lastName
                ? `${ this.firstName } ${ this.lastName }`
                : this.firstName
            : this.lastName
                ? this.lastName
                : ''
    }

    /*
     * Determines if the user has contact status set.
     */
    get hasContactStatus() {
        return Boolean(this.contactStatus && this.contactStatus.length)
    }

    /*
     * Determines if the user has an email address set.
     */
    get hasEmail() {
        return Boolean(this.email && this.email.length)
    }

    /*
     * Determines if the user has a name set.
     */
    get hasName() {
        return Boolean(this.firstName && this.firstName.length || this.lastName && this.lastName.length)
    }

    /*
     * Determines if the user has contact ongoing.
     */
    get isInOngoingContact() {
        return Boolean(this.hasContactStatus && this.contactStatus !== 'Contact Made')
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @static
     * @param {Object} data Source data.
     * @returns {User} Either the data is passed back if already of this type or the result of the constructor is.
     * @memberof User
     */
    static ensure(data) {
        return data && data instanceof User ? data : new User(data)
    }
}
